import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import { getAuthHeader } from "../lib/auth";

function apiUrl(path) {
  const base = String(api.base || "/api").replace(/\/$/, "");
  const suffix = String(path || "").startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}

async function requestJSON(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: getAuthHeader(),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    mode: "cors",
    credentials: "omit",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data?.message || data?.error || `Request failed (${response.status})`);
  }
  return data;
}

function formatTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function statusLabel(status) {
  const normalized = String(status || "new").toLowerCase();
  if (normalized === "answered") return "ANSWERED";
  if (normalized === "open") return "OPEN";
  return "NEW";
}

export default function QuestionAdmin() {
  const location = useLocation();
  const selectedId = useMemo(
    () => new URLSearchParams(location.search).get("question") || "",
    [location.search]
  );
  const [data, setData] = useState({ questions: [], counts: { new: 0, open: 0, answered: 0 } });
  const [filter, setFilter] = useState("new");
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await requestJSON("/tournaments/questions/admin/list");
      const questions = Array.isArray(result?.questions) ? result.questions : [];
      setData({
        questions,
        counts: result?.counts || { new: 0, open: 0, answered: 0 },
      });
      setAnswers((current) => {
        const next = { ...current };
        questions.forEach((question) => {
          if (typeof next[question.id] !== "string") next[question.id] = question.answer || "";
        });
        return next;
      });
    } catch (loadError) {
      setError(loadError?.message || "Could not load the question inbox.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedId || loading) return;
    const selected = data.questions.find((question) => question.id === selectedId);
    if (selected?.status) setFilter(selected.status);
    const timer = window.setTimeout(() => {
      document.getElementById(`question-${selectedId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [selectedId, loading, data.questions]);

  async function updateStatus(question, status) {
    setBusyId(question.id);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/questions/admin/${encodeURIComponent(question.id)}/status`, {
        method: "POST",
        body: { status },
      });
      setMessage(`Question marked ${status}.`);
      await load();
      setFilter(status);
    } catch (statusError) {
      setError(statusError?.message || "Could not update the question.");
    } finally {
      setBusyId("");
    }
  }

  async function sendResponse(question) {
    const answer = String(answers[question.id] || "").trim();
    if (!answer) {
      setError("Enter a response before sending.");
      return;
    }
    if (!window.confirm(`Send this response to ${question.email}?`)) return;

    setBusyId(question.id);
    setError("");
    setMessage("");
    try {
      const result = await requestJSON(
        `/tournaments/questions/admin/${encodeURIComponent(question.id)}/respond`,
        { method: "POST", body: { answer } }
      );
      setMessage(result?.message || "Response saved.");
      await load();
      setFilter("answered");
    } catch (responseError) {
      setError(responseError?.message || "Could not send the response.");
    } finally {
      setBusyId("");
    }
  }

  const visibleQuestions = data.questions.filter((question) => question.status === filter);

  return (
    <div className="page" style={styles.page}>
      <section style={styles.hero}>
        <div style={styles.eyebrow}>GAStreaming ADMIN</div>
        <h1 style={styles.title}>Question Inbox</h1>
        <p style={styles.subtitle}>
          Review guest and member questions, move them into progress, and send answers by no-reply email. Linked members also receive a bell notification that opens their response on the site.
        </p>
        <div style={styles.heroActions}>
          <Link to="/contact" style={styles.backLink}>← Back to Got Questions?</Link>
          <button type="button" onClick={load} style={styles.refreshButton} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh Inbox"}
          </button>
        </div>
      </section>

      <div style={styles.filterRow}>
        {[
          ["new", "New", data.counts?.new || 0],
          ["open", "Open", data.counts?.open || 0],
          ["answered", "Answered", data.counts?.answered || 0],
        ].map(([value, label, count]) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            style={{ ...styles.filterButton, ...(filter === value ? styles.filterButtonActive : {}) }}
          >
            {label} <span style={styles.countBubble}>{count}</span>
          </button>
        ))}
      </div>

      {message ? <div style={styles.success}>{message}</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}

      {loading ? (
        <div style={styles.empty}>Loading questions…</div>
      ) : visibleQuestions.length === 0 ? (
        <div style={styles.empty}>No {filter} questions right now.</div>
      ) : (
        <section style={styles.list}>
          {visibleQuestions.map((question) => {
            const busy = busyId === question.id;
            const highlighted = selectedId === question.id;
            return (
              <article
                key={question.id}
                id={`question-${question.id}`}
                style={{ ...styles.card, ...(highlighted ? styles.cardHighlighted : {}) }}
              >
                <div style={styles.cardTop}>
                  <div>
                    <div style={styles.category}>{question.categoryLabel || "Question"}</div>
                    <h2 style={styles.person}>{question.name || question.username || "Guest"}</h2>
                  </div>
                  <span style={question.status === "answered" ? styles.answeredPill : question.status === "open" ? styles.openPill : styles.newPill}>
                    {statusLabel(question.status)}
                  </span>
                </div>

                <div style={styles.metaGrid}>
                  <Meta label="Email" value={question.email || "—"} />
                  <Meta label="Username" value={question.username || "Guest / none"} />
                  <Meta label="Profile" value={question.linkedToAccount ? "Linked GAStreaming profile" : "Guest submission"} />
                  <Meta label="Submitted" value={formatTime(question.createdAt)} />
                </div>

                <div style={styles.questionBox}>
                  <div style={styles.metaLabel}>QUESTION</div>
                  <div style={styles.questionText}>{question.message}</div>
                </div>

                {question.status === "answered" ? (
                  <>
                    <div style={styles.answerBox}>
                      <div style={styles.metaLabel}>SENT RESPONSE</div>
                      <div style={styles.questionText}>{question.answer}</div>
                      <div style={styles.answerMeta}>
                        Answered {formatTime(question.answeredAt)}
                        {question.answeredByUsername ? ` by ${question.answeredByUsername}` : ""}
                      </div>
                    </div>
                    <details style={styles.resendDetails}>
                      <summary style={styles.resendSummary}>Edit or resend this response</summary>
                      <div style={styles.resendBody}>
                        <textarea
                          value={answers[question.id] || ""}
                          onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))}
                          rows={6}
                          maxLength={6000}
                          disabled={busy}
                          style={styles.textarea}
                        />
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => sendResponse(question)}
                          style={styles.sendButton}
                        >
                          {busy ? "Sending…" : "Send Updated Response"}
                        </button>
                      </div>
                    </details>
                  </>
                ) : (
                  <div style={styles.adminControls}>
                    {question.status === "new" ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => updateStatus(question, "open")}
                        style={styles.openButton}
                      >
                        Mark Open / In Progress
                      </button>
                    ) : null}

                    <label style={styles.label}>
                      Your response
                      <textarea
                        value={answers[question.id] || ""}
                        onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))}
                        rows={7}
                        maxLength={6000}
                        disabled={busy}
                        style={styles.textarea}
                        placeholder="Type the answer that should be emailed to this person and shown in their GAStreaming notification response page."
                      />
                    </label>

                    <div style={styles.sendRow}>
                      <span style={styles.deliveryText}>
                        {question.linkedToAccount
                          ? "Sends email + GAStreaming bell notification"
                          : "Sends no-reply email to guest"}
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => sendResponse(question)}
                        style={styles.sendButton}
                      >
                        {busy ? "Sending…" : "Send Response"}
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div style={styles.metaCard}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={styles.metaValue}>{value}</div>
    </div>
  );
}

const styles = {
  page: { maxWidth: 1240, margin: "0 auto", padding: "18px 16px 80px" },
  hero: { border: "1px solid rgba(210,40,52,.35)", borderRadius: 24, padding: "28px clamp(18px,4vw,44px)", background: "linear-gradient(135deg, rgba(80,8,15,.58), rgba(10,10,12,.92))", boxShadow: "0 20px 60px rgba(0,0,0,.35)" },
  eyebrow: { color: "#ff9aa2", fontWeight: 900, letterSpacing: ".16em", fontSize: 12 },
  title: { margin: "8px 0 8px", color: "#fff", fontSize: "clamp(2.2rem,7vw,5rem)", lineHeight: .98 },
  subtitle: { margin: 0, color: "rgba(255,255,255,.75)", fontSize: "clamp(1rem,2.2vw,1.2rem)", lineHeight: 1.6, maxWidth: 940 },
  heroActions: { marginTop: 20, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" },
  backLink: { textDecoration: "none", color: "#fff", border: "1px solid rgba(255,255,255,.18)", borderRadius: 999, padding: "10px 14px", fontWeight: 800, background: "rgba(255,255,255,.06)" },
  refreshButton: { border: "1px solid rgba(255,255,255,.17)", borderRadius: 999, background: "rgba(255,255,255,.06)", color: "#fff", padding: "10px 14px", fontWeight: 800, cursor: "pointer" },
  filterRow: { marginTop: 18, display: "flex", flexWrap: "wrap", gap: 10 },
  filterButton: { border: "1px solid rgba(255,255,255,.15)", borderRadius: 999, background: "rgba(255,255,255,.055)", color: "rgba(255,255,255,.8)", padding: "10px 14px", fontWeight: 900, cursor: "pointer" },
  filterButtonActive: { background: "rgba(153,10,22,.48)", color: "#fff", border: "1px solid rgba(255,77,88,.5)" },
  countBubble: { marginLeft: 7, display: "inline-flex", minWidth: 22, height: 22, borderRadius: 999, alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,.32)", fontSize: 12 },
  list: { display: "grid", gap: 16, marginTop: 18 },
  card: { border: "1px solid rgba(221,53,65,.28)", borderRadius: 20, padding: 18, background: "linear-gradient(145deg, rgba(35,8,12,.91), rgba(7,7,9,.97))", boxShadow: "0 18px 55px rgba(0,0,0,.34)" },
  cardHighlighted: { boxShadow: "0 0 0 2px rgba(255,174,67,.9), 0 18px 55px rgba(0,0,0,.45)" },
  cardTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14 },
  category: { color: "#ff9aa2", fontWeight: 900, letterSpacing: ".09em", textTransform: "uppercase", fontSize: 12 },
  person: { margin: "5px 0 0", color: "#fff", fontSize: "clamp(1.5rem,3.5vw,2.5rem)" },
  newPill: { borderRadius: 999, padding: "8px 11px", color: "#ffd27e", background: "rgba(125,69,2,.42)", border: "1px solid rgba(255,181,65,.4)", fontWeight: 900, fontSize: 12 },
  openPill: { borderRadius: 999, padding: "8px 11px", color: "#9ad6ff", background: "rgba(7,74,123,.4)", border: "1px solid rgba(72,172,240,.38)", fontWeight: 900, fontSize: 12 },
  answeredPill: { borderRadius: 999, padding: "8px 11px", color: "#98f0b5", background: "rgba(14,96,47,.34)", border: "1px solid rgba(67,217,121,.32)", fontWeight: 900, fontSize: 12 },
  metaGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10, marginTop: 16 },
  metaCard: { borderRadius: 12, border: "1px solid rgba(255,255,255,.09)", background: "rgba(255,255,255,.035)", padding: 12 },
  metaLabel: { color: "rgba(255,255,255,.48)", fontWeight: 900, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase" },
  metaValue: { color: "#fff", marginTop: 5, lineHeight: 1.45, overflowWrap: "anywhere" },
  questionBox: { marginTop: 14, borderRadius: 14, background: "rgba(255,255,255,.04)", padding: 14 },
  questionText: { color: "rgba(255,255,255,.88)", marginTop: 7, lineHeight: 1.65, whiteSpace: "pre-wrap" },
  answerBox: { marginTop: 16, borderRadius: 14, background: "rgba(23,111,55,.18)", border: "1px solid rgba(71,210,119,.25)", padding: 14 },
  answerMeta: { marginTop: 12, color: "rgba(185,247,202,.7)", fontSize: 12 },
  adminControls: { marginTop: 18, borderTop: "1px solid rgba(255,255,255,.08)", paddingTop: 16, display: "grid", gap: 14 },
  openButton: { justifySelf: "start", borderRadius: 999, border: "1px solid rgba(90,178,240,.4)", background: "rgba(10,81,129,.38)", color: "#ccecff", padding: "10px 14px", fontWeight: 900, cursor: "pointer" },
  label: { color: "rgba(255,255,255,.75)", fontWeight: 800, display: "grid", gap: 7 },
  textarea: { width: "100%", boxSizing: "border-box", borderRadius: 12, border: "1px solid rgba(255,255,255,.15)", background: "rgba(0,0,0,.52)", color: "#fff", padding: 12, resize: "vertical", font: "inherit", lineHeight: 1.55 },
  sendRow: { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
  deliveryText: { color: "rgba(255,255,255,.52)", fontSize: 13 },
  sendButton: { borderRadius: 999, border: "1px solid rgba(255,82,82,.7)", background: "linear-gradient(135deg,#da1724,#7e040c)", color: "#fff", padding: "11px 17px", fontWeight: 900, cursor: "pointer" },
  resendDetails: { marginTop: 12, borderRadius: 14, border: "1px solid rgba(255,255,255,.1)", background: "rgba(255,255,255,.035)", padding: "11px 13px" },
  resendSummary: { color: "rgba(255,255,255,.72)", fontWeight: 900, cursor: "pointer" },
  resendBody: { marginTop: 12, display: "grid", gap: 10, justifyItems: "end" },
  empty: { marginTop: 20, borderRadius: 18, padding: 24, textAlign: "center", color: "rgba(255,255,255,.62)", border: "1px dashed rgba(255,255,255,.15)", background: "rgba(0,0,0,.34)" },
  success: { marginTop: 16, borderRadius: 14, padding: 13, color: "#a8f3bf", background: "rgba(17,106,49,.25)", border: "1px solid rgba(61,203,106,.28)" },
  error: { marginTop: 16, borderRadius: 14, padding: 13, color: "#ffb7b7", background: "rgba(128,13,22,.26)", border: "1px solid rgba(255,76,88,.3)" },
};
